import { Node, Edge, Position } from 'reactflow';

/**
 * Generates automatic layout for flow diagram nodes
 * Uses a simple hierarchical layout algorithm
 */

interface LayoutConfig {
  nodeWidth: number;
  nodeHeight: number;
  horizontalSpacing: number;
  verticalSpacing: number;
}

const DEFAULT_CONFIG: LayoutConfig = {
  nodeWidth: 240,
  nodeHeight: 120,
  horizontalSpacing: 100,
  verticalSpacing: 80
};

export function generateFlowLayout(
  nodes: Node[],
  edges: Edge[],
  config: Partial<LayoutConfig> = {}
): { nodes: Node[], edges: Edge[] } {
  const layoutConfig = { ...DEFAULT_CONFIG, ...config };
  
  // Handle edge cases
  if (nodes.length === 0) {
    return { nodes: [], edges: [] };
  }
  
  if (nodes.length === 1) {
    // Single node - center it
    return {
      nodes: [{
        ...nodes[0],
        position: { x: 0, y: 0 }
      }],
      edges: []
    };
  }
  
  // For very simple linear flows (no data_flow specified), use simple layout
  if (edges.length > 0 && edges.every(e => e.id.startsWith('seq-'))) {
    return generateSimpleLinearLayout(nodes, edges, layoutConfig);
  }
  
  // Build adjacency list for traversal
  const adjacencyList = buildAdjacencyList(nodes, edges);
  const levels = assignNodeLevels(nodes, edges, adjacencyList);
  
  // Group nodes by level
  const nodesByLevel = new Map<number, Node[]>();
  nodes.forEach(node => {
    const level = levels.get(node.id) || 0;
    if (!nodesByLevel.has(level)) {
      nodesByLevel.set(level, []);
    }
    nodesByLevel.get(level)!.push(node);
  });
  
  // Calculate positions for each level
  const positionedNodes = nodes.map(node => {
    const level = levels.get(node.id) || 0;
    const nodesInLevel = nodesByLevel.get(level) || [];
    const nodeIndex = nodesInLevel.findIndex(n => n.id === node.id);
    
    // Calculate horizontal position to center the level
    const levelWidth = nodesInLevel.length * layoutConfig.nodeWidth + 
                      (nodesInLevel.length - 1) * layoutConfig.horizontalSpacing;
    const startX = -levelWidth / 2;
    const x = startX + nodeIndex * (layoutConfig.nodeWidth + layoutConfig.horizontalSpacing);
    
    // Calculate vertical position
    const y = level * (layoutConfig.nodeHeight + layoutConfig.verticalSpacing);
    
    return {
      ...node,
      position: { x, y },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top
    };
  });
  
  return { nodes: positionedNodes, edges };
}

/**
 * Generate simple linear layout for sequential flows
 */
function generateSimpleLinearLayout(
  nodes: Node[],
  edges: Edge[],
  config: LayoutConfig
): { nodes: Node[], edges: Edge[] } {
  // For linear flows, arrange nodes vertically in a single column
  const positionedNodes = nodes.map((node, index) => ({
    ...node,
    position: {
      x: 0,
      y: index * (config.nodeHeight + config.verticalSpacing)
    },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top
  }));
  
  return { nodes: positionedNodes, edges };
}

/**
 * Build adjacency list from edges
 */
function buildAdjacencyList(nodes: Node[], edges: Edge[]): Map<string, string[]> {
  const adjacencyList = new Map<string, string[]>();
  
  // Initialize all nodes
  nodes.forEach(node => {
    adjacencyList.set(node.id, []);
  });
  
  // Add edges
  edges.forEach(edge => {
    const sourceList = adjacencyList.get(edge.source) || [];
    sourceList.push(edge.target);
    adjacencyList.set(edge.source, sourceList);
  });
  
  return adjacencyList;
}

/**
 * Assign levels to nodes using BFS
 */
function assignNodeLevels(
  nodes: Node[], 
  edges: Edge[], 
  adjacencyList: Map<string, string[]>
): Map<string, number> {
  const levels = new Map<string, number>();
  const visited = new Set<string>();
  
  // Find nodes with no incoming edges (root nodes)
  const incomingEdges = new Map<string, number>();
  nodes.forEach(node => incomingEdges.set(node.id, 0));
  edges.forEach(edge => {
    incomingEdges.set(edge.target, (incomingEdges.get(edge.target) || 0) + 1);
  });
  
  const rootNodes = nodes.filter(node => incomingEdges.get(node.id) === 0);
  
  // If no root nodes found (circular graph), start with first node
  if (rootNodes.length === 0 && nodes.length > 0) {
    rootNodes.push(nodes[0]);
  }
  
  // BFS to assign levels
  const queue: { nodeId: string, level: number }[] = [];
  rootNodes.forEach(node => {
    queue.push({ nodeId: node.id, level: 0 });
    visited.add(node.id);
  });
  
  while (queue.length > 0) {
    const { nodeId, level } = queue.shift()!;
    levels.set(nodeId, level);
    
    const neighbors = adjacencyList.get(nodeId) || [];
    neighbors.forEach(neighborId => {
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        queue.push({ nodeId: neighborId, level: level + 1 });
      } else {
        // Update level if we found a longer path
        const currentLevel = levels.get(neighborId);
        if (currentLevel !== undefined && currentLevel < level + 1) {
          levels.set(neighborId, level + 1);
          // Re-process this node's children
          queue.push({ nodeId: neighborId, level: level + 1 });
        }
      }
    });
  }
  
  // Handle any unconnected nodes
  nodes.forEach(node => {
    if (!levels.has(node.id)) {
      levels.set(node.id, 0);
    }
  });
  
  return levels;
}

/**
 * Alternative layout for complex flows with multiple inputs/outputs
 */
export function generateComplexFlowLayout(
  nodes: Node[],
  edges: Edge[]
): { nodes: Node[], edges: Edge[] } {
  // Identify node types
  const inputNodes: Node[] = [];
  const processingNodes: Node[] = [];
  const outputNodes: Node[] = [];
  
  nodes.forEach(node => {
    const nodeType = node.data?.nodeType;
    if (nodeType === 'input') {
      inputNodes.push(node);
    } else if (nodeType === 'output') {
      outputNodes.push(node);
    } else {
      processingNodes.push(node);
    }
  });
  
  const nodeWidth = 240;
  const nodeHeight = 120;
  const horizontalSpacing = 100;
  const verticalSpacing = 100;
  
  // Position input nodes on the left
  const positionedInputs = inputNodes.map((node, index) => ({
    ...node,
    position: {
      x: 0,
      y: index * (nodeHeight + verticalSpacing)
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left
  }));
  
  // Position output nodes on the right
  const outputX = (processingNodes.length > 0 ? 2 : 1) * (nodeWidth + horizontalSpacing);
  const positionedOutputs = outputNodes.map((node, index) => ({
    ...node,
    position: {
      x: outputX,
      y: index * (nodeHeight + verticalSpacing)
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left
  }));
  
  // Position processing nodes in the middle
  const processingX = nodeWidth + horizontalSpacing;
  const positionedProcessing = processingNodes.map((node, index) => ({
    ...node,
    position: {
      x: processingX,
      y: index * (nodeHeight + verticalSpacing)
    },
    sourcePosition: Position.Right,
    targetPosition: Position.Left
  }));
  
  const allPositionedNodes = [
    ...positionedInputs,
    ...positionedProcessing,
    ...positionedOutputs
  ];
  
  return { nodes: allPositionedNodes, edges };
}